import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faCheck, faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../common/DataTable';
import Pagination from '../common/Pagination';
import ConfirmDialog from '../common/ConfirmDialog';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import customerPortalAdminService from '../../services/customerPortalAdminService';

const PAGE_SIZE = 20;

// Onboarding-status pill. `invited` = waiting on the customer to submit their
// profile; `profile_submitted` = waiting on staff review.
const OnboardingBadge = ({ status, labels }) => {
  const s = (status || '').toLowerCase();
  const isSubmitted = s === 'profile_submitted';
  const color = isSubmitted ? UI_COLORS.STATUS.INFO : UI_COLORS.STATUS.WARNING;
  const label = isSubmitted ? (labels?.pendingReview || 'Pending review') : (labels?.pendingCustomer || 'Pending customer action');
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap" style={{ color, backgroundColor: colorHelpers.withOpacity(color, 0.12) }}>
      {label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Super-admin review queue: customers with onboarding_status === 'profile_submitted'.
 * Approve unlocks the portal; Reject (with optional reason) is non-terminal.
 */
const CustomerApprovalsTab = ({ onViewCustomer, onAlertMessage, onCustomerChanged }) => {
  const { t } = useTranslation();
  const s = t('customers.approvals', { returnObjects: true });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerPortalAdminService.getPendingApproval({ skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE });
      if (res.success) {
        const data = res.data || {};
        setItems(Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []));
        setTotal(typeof data.total === 'number' ? data.total : 0);
      } else {
        setItems([]); setTotal(0);
        onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: res.error });
      }
    } catch (err) {
      setItems([]); setTotal(0);
      onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  }, [page, onAlertMessage, t]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (row) => {
    setActioningId(row.id);
    try {
      const res = await customerPortalAdminService.approve(row.id);
      if (res.success) {
        onAlertMessage?.({ type: 'success', title: t('common.status.success'), message: s?.approvedMsg || 'Customer approved.' });
        load();
        onCustomerChanged?.(); // keep the main customers list in sync
      } else {
        onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: res.error });
      }
    } catch (err) {
      onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setActioningId(null);
    }
  };

  const submitReject = async () => {
    const row = rejectTarget;
    setRejectTarget(null);
    setActioningId(row.id);
    try {
      const res = await customerPortalAdminService.reject(row.id, rejectReason.trim() || undefined);
      if (res.success) {
        onAlertMessage?.({ type: 'success', title: t('common.status.success'), message: s?.rejectedMsg || 'Customer rejected.' });
        load();
        onCustomerChanged?.();
      } else {
        onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: res.error });
      }
    } catch (err) {
      onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setActioningId(null);
      setRejectReason('');
    }
  };

  // Resend the invite for an `invited` row (fresh temp password). 429 = throttled
  // (password NOT rotated → the existing one still works).
  const handleResend = async (row) => {
    setActioningId(row.id);
    try {
      const res = await customerPortalAdminService.resendInvite(row.id);
      if (res.success) {
        const ch = res.data?.invited_channels || [];
        const msg = ch.length
          ? `${s?.resentMsg || 'Invite re-sent'} (${ch.join(', ')}).`
          : (s?.resendNoChannelMsg || 'No channel is enabled — nothing was sent.');
        onAlertMessage?.({ type: ch.length ? 'success' : 'warning', title: t('common.status.success'), message: msg });
      } else if (res.status === 429) {
        onAlertMessage?.({ type: 'warning', title: t('common.status.warning'), message: s?.resendThrottledMsg || 'Invited very recently — try again shortly. The existing password still works.' });
      } else {
        onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: res.error });
      }
    } catch (err) {
      onAlertMessage?.({ type: 'error', title: t('common.status.error'), message: err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setActioningId(null);
    }
  };

  const columns = useMemo(() => [
    { id: 'company_name', label: s?.columns?.company || 'Company', renderCell: (c) => (
      <div>
        <div className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{c.company_name || '—'}</div>
        <div className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{c.owner_name || ''}</div>
      </div>
    ) },
    { id: 'gst_number', label: s?.columns?.gst || 'GST', renderCell: (c) => <span className="text-sm">{c.gst_number || '—'}</span> },
    { id: 'location', label: s?.columns?.location || 'Location', renderCell: (c) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{[c.city, c.state].filter(Boolean).join(', ') || '—'}</span> },
    { id: 'onboarding_status', label: s?.columns?.status || 'Status', renderCell: (c) => <OnboardingBadge status={c.onboarding_status} labels={s?.badges} /> },
    { id: 'updated_at', label: s?.columns?.submitted || 'Submitted', renderCell: (c) => <span className="text-sm">{formatDate(c.updated_at || c.created_at)}</span> },
    { id: 'actions', label: s?.columns?.actions || 'Actions', align: 'right', renderCell: (c) => {
      const isSubmitted = (c.onboarding_status || '').toLowerCase() === 'profile_submitted';
      const busyRow = actioningId === c.id;
      return (
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => onViewCustomer?.(c.id)} className="p-1.5 rounded-md hover:bg-gray-100" title={s?.view || 'View'} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            <FontAwesomeIcon icon={faEye} />
          </button>
          {isSubmitted ? (
            // profile_submitted → staff can Approve (approve requires this state)
            <button type="button" disabled={busyRow} onClick={() => handleApprove(c)} className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50" title={s?.approve || 'Approve'} style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1) }}>
              <FontAwesomeIcon icon={faCheck} className="mr-1" />{s?.approve || 'Approve'}
            </button>
          ) : (
            // invited → nothing to approve yet; offer Resend invite
            <button type="button" disabled={busyRow} onClick={() => handleResend(c)} className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50" title={s?.resend || 'Resend invite'} style={{ color: UI_COLORS.BRAND.SECONDARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.SECONDARY, 0.1) }}>
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />{s?.resend || 'Resend'}
            </button>
          )}
          {/* Reject works on both invited (cancel) and profile_submitted */}
          <button type="button" disabled={busyRow} onClick={() => { setRejectTarget(c); setRejectReason(''); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50" title={isSubmitted ? (s?.reject || 'Reject') : (s?.cancel || 'Cancel')} style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}>
            <FontAwesomeIcon icon={faTimes} className="mr-1" />{isSubmitted ? (s?.reject || 'Reject') : (s?.cancel || 'Cancel')}
          </button>
        </div>
      );
    } },
  ], [s, actioningId, onViewCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mt-2">
      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        keyField="id"
        enablePagination={false}
        emptyState={{ title: s?.empty?.title || 'Nothing to review', message: s?.empty?.message || 'Invited and submitted customers awaiting action appear here.' }}
      />

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={total}
        onPageChange={setPage}
        loading={loading}
        totalLabel={s?.totalLabel || 'pending customers'}
        pageSize={PAGE_SIZE}
      />

      <ConfirmDialog
        isOpen={!!rejectTarget}
        title={(rejectTarget && (rejectTarget.onboarding_status || '').toLowerCase() !== 'profile_submitted') ? (s?.cancelTitle || 'Cancel invite') : (s?.rejectTitle || 'Reject profile')}
        message={(
          <div className="space-y-2">
            <p>{(rejectTarget && (rejectTarget.onboarding_status || '').toLowerCase() !== 'profile_submitted') ? (s?.cancelMessage || 'Cancel this invite? The customer can be re-invited later.') : (s?.rejectMessage || 'Reject this customer’s submitted profile? They can fix and resubmit.')}</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              placeholder={s?.reasonPlaceholder || 'Reason (optional)'}
              className="w-full px-3 py-2 border-2 rounded-lg text-sm"
              style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
            />
          </div>
        )}
        confirmLabel={(rejectTarget && (rejectTarget.onboarding_status || '').toLowerCase() !== 'profile_submitted') ? (s?.cancel || 'Cancel invite') : (s?.reject || 'Reject')}
        confirmVariant="danger"
        onConfirm={submitReject}
        onCancel={() => { setRejectTarget(null); setRejectReason(''); }}
      />
    </div>
  );
};

export default CustomerApprovalsTab;
